// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/services/productService.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 4.2 – FEAT: Listagem de Fornecedores
 * Impacto da Versão: [src/services/productService.js]
 * Data: 05/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [ADICIONADO]: Método `getAllSuppliers` para alimentar o dropdown no formulário de produtos.
 */
//

import { db, storage } from '../firebase/config';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  limit,
  runTransaction,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// [GS-NOVO] Constantes de Produção para Cálculo de Mão de Obra
const PRODUCTION_CONSTANTS = {
  MINIMUM_WAGE: 1518.00,
  MONTHLY_HOURS: 176,
  ASSEMBLY_TIME_MINUTES: 20
};

class ProductService {
  constructor() {
    this.productsCollectionRef = collection(db, 'products');
    this.metaCollectionRef = collection(db, 'meta');
    this.categoriesCollectionRef = collection(db, 'categories');
  }

  uploadImage = async (file, path = 'product_images/') => {
    if (!file) throw new Error('Nenhum arquivo fornecido para upload.');
    const timestamp = Date.now();
    const uniqueFileName = `${path}${timestamp}-${file.name}`;
    const storageRef = ref(storage, uniqueFileName);
    const uploadResult = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(uploadResult.ref);
    return downloadURL;
  };

  getProductBySupplierItemCode = async (supplierItemCode) => {
    if (!supplierItemCode) return null;
    const q = query(
      this.productsCollectionRef,
      where('supplierItemCode', '==', supplierItemCode),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  };

  getAllCategories = async () => {
    const snapshot = await getDocs(this.categoriesCollectionRef);
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  };

  // [GS-NOVO] Método para listar fornecedores no dropdown
  getAllSuppliers = async () => {
    const suppliersCollectionRef = collection(db, 'suppliers');
    const snapshot = await getDocs(suppliersCollectionRef);
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  };

  getNextProductSequentialNum = async (categoryId, supplierId) => {
    const safeCat = categoryId || 'GENERIC_CAT';
    const safeSup = supplierId || 'GENERIC_SUP';

    const counterDocRef = doc(
      this.metaCollectionRef,
      'product_sequential_counters'
    );
    let nextNumber = 1;

    await runTransaction(db, async (transaction) => {
      const sfDoc = await transaction.get(counterDocRef);
      const counterPath = `${safeCat}.${safeSup}`;

      if (
        !sfDoc.exists() ||
        !sfDoc.data()[safeCat] ||
        !sfDoc.data()[safeCat][safeSup]
      ) {
        const updateData = sfDoc.exists() ? sfDoc.data() : {};
        if (!updateData[safeCat]) updateData[safeCat] = {};
        updateData[safeCat][safeSup] = nextNumber;
        transaction.set(counterDocRef, updateData);
      } else {
        nextNumber = sfDoc.data()[safeCat][safeSup] + 1;
        transaction.update(counterDocRef, { [counterPath]: nextNumber });
      }
    });
    return nextNumber;
  };

  async getCategoryPrefix(categoryId) {
    if (!categoryId) return 'GEN';
    try {
      const refCat = doc(db, 'categories', categoryId);
      const snap = await getDoc(refCat);
      if (snap.exists()) {
        const data = snap.data();
        return (
          data.code ||
          data.prefix ||
          (data.name ? data.name.substring(0, 3) : 'GEN')
        ).toUpperCase();
      }
    } catch (e) {
      console.error('Erro ao buscar categoria:', e);
    }
    return 'GEN';
  }

  async getSupplierCode(supplierId) {
    if (!supplierId) return '000';
    try {
      const refSup = doc(db, 'suppliers', supplierId);
      const snap = await getDoc(refSup);
      if (snap.exists()) {
        const data = snap.data();
        // [GS-FIX]: Prioriza friendlyCode (ex: 013) usado no sistema atual
        return data.friendlyCode || data.code || data.supplierCode || '000';
      }
    } catch (e) {
      console.error('Erro ao buscar fornecedor:', e);
    }
    return '000';
  }

  async generateSmartSku(categoryId, supplierId) {
    const prefix = await this.getCategoryPrefix(categoryId);
    const supplierCode = await this.getSupplierCode(supplierId);
    const sequence = await this.getNextProductSequentialNum(
      categoryId,
      supplierId
    );

    const formattedSupplier = String(supplierCode).padStart(3, '0');
    const formattedSequence = String(sequence).padStart(3, '0');

    return `${prefix}${formattedSupplier}${formattedSequence}`;
  }

  addProduct = async (newProduct) => {
    const productToSave = {
      name: newProduct.name || '',
      supplierProductName:
        newProduct.supplierProductName || newProduct.name || '',
      description: newProduct.description || '',
      sku: newProduct.sku || '',
      productType: newProduct.productType || 'venda',
      
      // [GS-NOVO] Mapeamento Financeiro
      costType: newProduct.costType || 'PRONTA',
      price: parseFloat(newProduct.price) || 0.0, // Preço Decidido
      suggestedPrice: parseFloat(newProduct.suggestedPrice) || 0.0, // Preço Sugerido
      cost: parseFloat(newProduct.cost) || 0.0,
      costs: newProduct.costs || {}, // Detalhamento (frete, impostos)

      stock: parseFloat(newProduct.stock) || 0,

      isActive: true,
      pdvEnabled: newProduct.pdvEnabled ?? false,
      displaySettings: newProduct.displaySettings || {
        onHomepage: false,
        onSeparamos: false,
      },
      isRecommended:
        newProduct.displaySettings?.onSeparamos ??
        newProduct.isRecommended ??
        false,

      dimensions:
        newProduct.dimensions || {
          height: 0,
          length: 0,
          width: 0,
          weight: 0,
        },
      images: newProduct.images || [],
      categoryId: newProduct.categoryId || null,
      supplierId: newProduct.supplierId || null, // [GS-FIX] Garantindo persistência do ID
      supplierItemCode: newProduct.supplierItemCode || '',
      location: newProduct.location || 'Em Loja',
      status: newProduct.status || 'Disponível',
      parentSku: newProduct.parentSku || null,
      isVariant: newProduct.isVariant || false,

      productionMode: newProduct.productionMode || null,
      rawCost: parseFloat(newProduct.rawCost) || 0.0,
      platingCost: parseFloat(newProduct.platingCost) || 0.0,
      goldGramPrice: parseFloat(newProduct.goldGramPrice) || 0.0,
      rawPurchaseId: newProduct.rawPurchaseId || null,
      platingPurchaseId: newProduct.platingPurchaseId || null,
      supplierRawId: newProduct.supplierRawId || null,
      supplierPlatingId: newProduct.supplierPlatingId || null,

      lotId: newProduct.lotId || null,
      lotItemId: newProduct.lotItemId || null,
      nfId: newProduct.nfId || null,
      nfNumber: newProduct.nfNumber || null,

      isTechnical: newProduct.isTechnical ?? false,

      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const docRef = await addDoc(this.productsCollectionRef, productToSave);
    return docRef;
  };

  updateProduct = async (id, updatedProduct) => {
    const productDoc = doc(db, 'products', id);

    const productToUpdate = {
      ...updatedProduct,
      supplierProductName:
        updatedProduct.supplierProductName || updatedProduct.name || '',

      // [GS-NOVO] Mapeamento Financeiro Update
      costType: updatedProduct.costType, 
      price: parseFloat(updatedProduct.price) || 0.0,
      suggestedPrice: parseFloat(updatedProduct.suggestedPrice) || 0.0,
      cost: parseFloat(updatedProduct.cost) || 0.0,
      // Nota: Não sobrescrevemos 'costs' inteiro se não vier, para não perder histórico se parcial
      ...(updatedProduct.costs && { costs: updatedProduct.costs }),

      stock: parseFloat(updatedProduct.stock) || 0,
      dimensions:
        updatedProduct.dimensions || {
          height: 0,
          length: 0,
          width: 0,
          weight: 0,
        },
      location: updatedProduct.location || 'Em Loja',
      status: updatedProduct.status || 'Disponível',
      isActive: true,
      supplierId: updatedProduct.supplierId || null, // [GS-FIX] Atualização do Fornecedor

      pdvEnabled: updatedProduct.pdvEnabled ?? false,
      displaySettings: {
        onHomepage:
          updatedProduct.displaySettings?.onHomepage ??
          updatedProduct.onHomepage ??
          false,
        onSeparamos:
          updatedProduct.displaySettings?.onSeparamos ??
          updatedProduct.isRecommended ??
          false,
      },
      isRecommended:
        updatedProduct.displaySettings?.onSeparamos ??
        updatedProduct.isRecommended ??
        false,

      productionMode: updatedProduct.productionMode ?? null,
      rawCost: parseFloat(updatedProduct.rawCost ?? 0) || 0.0,
      platingCost: parseFloat(updatedProduct.platingCost ?? 0) || 0.0,
      goldGramPrice: parseFloat(updatedProduct.goldGramPrice ?? 0) || 0.0,
      rawPurchaseId: updatedProduct.rawPurchaseId ?? null,
      platingPurchaseId: updatedProduct.platingPurchaseId ?? null,
      supplierRawId: updatedProduct.supplierRawId ?? null,
      supplierPlatingId: updatedProduct.supplierPlatingId ?? null,
      lotId: updatedProduct.lotId ?? null,
      lotItemId: updatedProduct.lotItemId ?? null,
      nfId: updatedProduct.nfId ?? null,
      nfNumber: updatedProduct.nfNumber ?? null,

      isTechnical: updatedProduct.isTechnical ?? false,

      updatedAt: new Date(),
    };

    // Remove campos undefined para não gravar sujeira
    Object.keys(productToUpdate).forEach(key => productToUpdate[key] === undefined && delete productToUpdate[key]);

    if (productToUpdate.id) delete productToUpdate.id;
    await updateDoc(productDoc, productToUpdate);
  };

  // [GS-NOVO] Método de Montagem (Kitting) com Transação Atômica
  createAssemblyProduct = async (productData, components, qtyToProduce) => {
    const qty = parseFloat(qtyToProduce) || 0;
    if (qty <= 0) throw new Error("Quantidade a produzir inválida.");
    if (!components || components.length === 0) throw new Error("Lista de componentes vazia.");

    // 1. Gera o SKU fora da transação (para evitar conflito de contadores)
    const newSku = await this.generateSmartSku(productData.categoryId, productData.supplierId);

    // 2. Calcula Mão de Obra Unitária
    const laborCostUnit = (PRODUCTION_CONSTANTS.MINIMUM_WAGE / PRODUCTION_CONSTANTS.MONTHLY_HOURS) * (PRODUCTION_CONSTANTS.ASSEMBLY_TIME_MINUTES / 60);

    return runTransaction(db, async (transaction) => {
      let totalComponentsCostUnit = 0;

      // 3. Verifica Estoque e Calcula Custo dos Componentes
      for (const comp of components) {
        const compRef = doc(db, 'products', comp.id);
        const compDoc = await transaction.get(compRef);
        
        if (!compDoc.exists()) throw new Error(`Componente ${comp.id} não encontrado.`);
        
        const compData = compDoc.data();
        const currentStock = parseFloat(compData.stock) || 0;
        const requiredStock = comp.qtyUsed * qty;

        if (currentStock < requiredStock) {
          throw new Error(`Estoque insuficiente para ${compData.name} (SKU: ${compData.sku}). Necessário: ${requiredStock}, Disponível: ${currentStock}.`);
        }

        // Custo Médio do Componente
        const compCost = parseFloat(compData.cost) || 0;
        totalComponentsCostUnit += (compCost * comp.qtyUsed);

        // Baixa no Estoque (Reserva/Consumo Imediato)
        transaction.update(compRef, { 
          stock: currentStock - requiredStock,
          updatedAt: new Date()
        });
      }

      // 4. Cria o Novo Produto Montado
      const finalCostUnit = totalComponentsCostUnit + laborCostUnit;
      
      const newProductRef = doc(collection(db, 'products'));
      
      const newProductPayload = {
        ...productData,
        sku: newSku,
        costType: 'MONTAGEM', // Força o tipo
        cost: finalCostUnit,
        stock: qty,
        status: productData.status || 'Disponível', // Já nasce disponível se desejar
        pdvEnabled: true, // Produto montado geralmente vai pro PDV
        createdAt: new Date(),
        updatedAt: new Date(),
        
        // Memória de Cálculo (Opcional, mas útil para auditoria)
        costs: {
            componentsCost: totalComponentsCostUnit,
            laborCost: laborCostUnit,
            productionParams: PRODUCTION_CONSTANTS
        }
      };
      
      // Limpeza de campos vazios/indesejados
      if (!newProductPayload.supplierProductName) newProductPayload.supplierProductName = newProductPayload.name;
      delete newProductPayload.id;

      transaction.set(newProductRef, newProductPayload);
      
      return { id: newProductRef.id, sku: newSku, cost: finalCostUnit };
    });
  };

  consolidateProduct = async (id, overrides = {}) => {
    const productDocRef = doc(db, 'products', id);

    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(productDocRef);
      if (!snap.exists()) {
        throw new Error('Produto não encontrado para consolidação.');
      }

      const data = snap.data() || {};

      const rawCost = parseFloat(data.rawCost ?? 0) || 0.0;
      const platingCost = parseFloat(data.platingCost ?? 0) || 0.0;
      const baseCostFromParts = rawCost + platingCost;

      const currentCost = parseFloat(data.cost ?? 0) || 0.0;
      const currentPrice = parseFloat(data.price ?? 0) || 0.0;

      const finalCost =
        overrides.cost != null
          ? parseFloat(overrides.cost) || 0.0
          : baseCostFromParts > 0
          ? baseCostFromParts
          : currentCost;

      const finalPrice =
        overrides.price != null
          ? parseFloat(overrides.price) || 0.0
          : currentPrice;

      let finalSku = data.sku;
      if (!finalSku || String(finalSku).trim() === '') {
        const timestamp = Date.now().toString(36).toUpperCase();
        finalSku = `GS-PROD-${timestamp}`;
      }

      const finalProductionMode =
        overrides.productionMode ||
        data.productionMode ||
        (rawCost > 0 || platingCost > 0 ? 'BRUTO_BANHO' : null);

      const updateData = {
        ...data,
        sku: finalSku,
        cost: finalCost,
        price: finalPrice,
        productionMode: finalProductionMode,
        updatedAt: new Date(),
      };

      delete updateData.id;

      transaction.update(productDocRef, updateData);
      return { id: snap.id, ...updateData };
    });
  };

  upsertTechnicalProductFromLotItem = async (payload) => {
    const {
      lotId,
      lotCode,
      nfId,
      nfNumber,
      categoryId,
      supplierId,
      lotItemId,
      description,
      quantity,
      costBruto,
      costFinal,
      weightGrams,
      bathPricePerGram,
      existingProductId,
    } = payload || {};

    const qty = Number(quantity) || 0;
    if (!qty || qty <= 0) {
      return null;
    }

    const rawCostPerPiece = Number(costBruto) || 0;
    const finalCostPerPiece = Number(costFinal) || 0;
    const weight = Number(weightGrams) || 0;
    const goldGramPrice = Number(bathPricePerGram) || 0;

    const platingCostPerPiece =
      weight > 0 && goldGramPrice > 0
        ? weight * goldGramPrice
        : Math.max(finalCostPerPiece - rawCostPerPiece, 0);

    // --- ATUALIZAÇÃO (UPDATE) ---
    if (existingProductId) {
      const productDocRef = doc(db, 'products', existingProductId);
      const snap = await getDoc(productDocRef);

      if (snap.exists()) {
        const data = snap.data() || {};
        const prevStock = Number(data.stock ?? 0) || 0;
        const prevCost = Number(data.cost ?? 0) || 0;

        const totalCostBefore = prevStock * prevCost;
        const totalCostAdded = finalCostPerPiece * qty;
        const newStock = prevStock + qty;
        const newCost =
          newStock > 0
            ? (totalCostBefore + totalCostAdded) / newStock
            : finalCostPerPiece;

        const updated = {
          ...data,
          stock: newStock,
          cost: newCost,
          productionMode: data.productionMode || 'BRUTO_BANHO',
          rawCost: rawCostPerPiece,
          platingCost: platingCostPerPiece,
          goldGramPrice: goldGramPrice || data.goldGramPrice || 0,
          lotId: lotId || data.lotId || null,
          lotItemId: lotItemId || data.lotItemId || null,
          nfId: nfId || data.nfId || null,
          nfNumber: nfNumber || data.nfNumber || null,
          isTechnical: data.isTechnical ?? true,
          updatedAt: new Date(),
        };

        await updateDoc(productDocRef, updated);

        return {
          productId: existingProductId,
          sku: updated.sku || data.sku || '',
          quantityBefore: prevStock,
          quantityAfter: newStock,
        };
      }
    }

    // --- CRIAÇÃO DE NOVO PRODUTO TÉCNICO (CREATE) ---
    let generatedSku = '';
    if (categoryId && supplierId) {
      generatedSku = await this.generateSmartSku(categoryId, supplierId);
    }

    const technicalName =
      (description && String(description).trim()) ||
      `Peça pós-banho ${lotCode || lotId || ''}`.trim();

    const baseProduct = {
      name: '', 
      supplierProductName: technicalName,
      description: `Produto técnico pós-banho. Lote ${lotCode || ''} – NF ${
        nfNumber || ''
      }.`,

      sku: generatedSku,

      productType: 'venda',
      price: 0,
      cost: finalCostPerPiece,
      stock: qty,
      images: [],
      categoryId: categoryId || null,
      supplierItemCode: '',
      location: 'Estoque Técnico',
      status: 'Aguardando Destinação',
      parentSku: null,
      isVariant: false,
      isActive: true,
      pdvEnabled: false,
      displaySettings: { onHomepage: false, onSeparamos: false },
      isRecommended: false,
      productionMode: 'BRUTO_BANHO',
      rawCost: rawCostPerPiece,
      platingCost: platingCostPerPiece,
      goldGramPrice: goldGramPrice,
      rawPurchaseId: null,
      platingPurchaseId: null,
      supplierRawId: supplierId || null,
      supplierPlatingId: null,
      lotId: lotId || null,
      lotItemId: lotItemId || null,
      nfId: nfId || null,
      nfNumber: nfNumber || null,
      isTechnical: true,
    };

    const createdRef = await this.addProduct(baseProduct);
    const createdId = createdRef.id;

    const consolidated = await this.consolidateProduct(createdId, {
      cost: finalCostPerPiece,
      productionMode: 'BRUTO_BANHO',
    });

    return {
      productId: createdId,
      sku: consolidated.sku || '',
      quantityBefore: 0,
      quantityAfter: qty,
    };
  };

  deleteProduct = async (id) => {
    const productDoc = doc(db, 'products', id);
    await deleteDoc(productDoc);
  };

  getProductsForPOS = async () => {
    const snapshot = await getDocs(this.productsCollectionRef);
    return snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((p) => {
        // [GS-CORRECAO: Filtro Estrito para PDV]
        const inPOS = p.pdvEnabled === true;
        const status = p.status || 'Disponível';
        return inPOS && status === 'Disponível';
      });
  };

  canEnableSeparamos = async () => {
    const snapshot = await getDocs(this.productsCollectionRef);
    const ids = new Set();
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const ds = data.displaySettings || {};
      const flagged = !!ds.onSeparamos || !!data.isRecommended;
      if (flagged) {
        ids.add(docSnap.id);
      }
    });
    return ids.size < 5;
  };

  getVariantsByParentSku = async (parentSku) => {
    const q = query(
      this.productsCollectionRef,
      where('parentSku', '==', parentSku)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  };

  getAllProducts = async () => {
    const querySnapshot = await getDocs(this.productsCollectionRef);
    return querySnapshot.docs;
  };

  getProduct = async (id) => {
    const productDoc = doc(db, 'products', id);
    const docSnap = await getDoc(productDoc);
    if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() };
    return null;
  };
}

const productService = new ProductService();
export default productService;